import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.ts';
import SearchSelect from './SearchSelect.tsx';

/*
 * Pick a board, or set up one never had before (type, GSM, sheet size) in
 * place. Used wherever board is chosen before it arrives: ordering it, and
 * choosing it for a job from To order. A board typed in that is already in
 * the list is picked rather than made twice.
 */

export interface ChooserBoard { item_id: number; name: string; hint?: string }
interface BoardType { id: number; name: string; active: boolean }

export default function BoardChooser({ id, boards, value, onChange, onCreated, canAdd, autoFocus }: {
  id: string;
  boards: ChooserBoard[];
  value: string;
  onChange: (itemId: string) => void;
  onCreated: (b: ChooserBoard) => void;
  canAdd: boolean;
  autoFocus?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [types, setTypes] = useState<BoardType[]>([]);
  const [typeId, setTypeId] = useState('');
  const [newType, setNewType] = useState('');
  const [typing, setTyping] = useState(false);
  const [gsm, setGsm] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!adding || types.length) return;
    api.get<{ types: BoardType[] }>('/api/stock/board-types').then((r) => {
      const active = r.types.filter((t) => t.active);
      setTypes(active);
      if (active.length === 1) setTypeId(String(active[0]!.id));
    }).catch(() => setError('Could not load the board types'));
  }, [adding, types.length]);

  const type = types.find((t) => String(t.id) === typeId);
  const l = Number(length);
  const w = Number(width);
  const name = type && Number(gsm) > 0 && l > 0 && w > 0
    ? `${type.name} · ${Number(gsm)} GSM · ${Math.max(l, w)} × ${Math.min(l, w)} cm` : '';
  const existing = name ? boards.find((b) => b.name === name) : undefined;
  const problem = !type ? 'Choose the board type'
    : !(Number(gsm) > 0) ? 'Enter the GSM'
    : !(l > 0 && w > 0) ? 'Enter the sheet size' : '';

  function close() {
    setAdding(false); setError(''); setGsm(''); setLength(''); setWidth('');
  }

  async function saveType() {
    setBusy(true); setError('');
    try {
      const r = await api.post<{ type: BoardType }>('/api/stock/board-types', { name: newType.trim() });
      setTypes((t) => [...t, r.type].sort((a, b) => a.name.localeCompare(b.name)));
      setTypeId(String(r.type.id));
      setNewType(''); setTyping(false);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the type'); }
    finally { setBusy(false); }
  }

  async function add() {
    if (problem) return;
    if (existing) { onChange(String(existing.item_id)); close(); return; }
    setBusy(true); setError('');
    try {
      const r = await api.post<{ item: { item_id: number; name: string } }>('/api/stock/board-items', {
        boardTypeId: Number(typeId), gsm: Number(gsm), lengthCm: l, widthCm: w,
      });
      onCreated({ item_id: r.item.item_id, name: r.item.name });
      onChange(String(r.item.item_id));
      close();
    } catch (e) {
      const d = e instanceof ApiError ? e.details as { itemId?: number; retired?: boolean } | undefined : undefined;
      if (d?.itemId && !d.retired) { onChange(String(d.itemId)); close(); return; }
      setError(e instanceof Error ? e.message : 'Could not add the board');
    } finally { setBusy(false); }
  }

  if (!adding) {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <SearchSelect id={id} value={value} autoFocus={autoFocus} onChange={onChange}
                      placeholder="Type to search, e.g. 230 or 61" emptyLabel="Choose the board…"
                      options={boards.map((b) => ({ value: String(b.item_id), label: b.name, hint: b.hint }))} />
        {canAdd && (
          <button className="btn" style={{ whiteSpace: 'nowrap' }} onClick={() => setAdding(true)}>
            + New board
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="new-board">
      {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="field">
        <label htmlFor={`${id}-type`} className="req">Board type</label>
        {typing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input id={`${id}-type`} type="text" value={newType} autoFocus placeholder="e.g. White Back"
                   onChange={(e) => setNewType(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter' && newType.trim()) void saveType(); }} />
            <button className="btn primary" disabled={busy || !newType.trim()} onClick={() => void saveType()}>Add</button>
            <button className="btn" onClick={() => { setTyping(false); setNewType(''); }}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <select id={`${id}-type`} value={typeId} autoFocus onChange={(e) => setTypeId(e.target.value)}>
              <option value="">Choose…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button className="btn" style={{ whiteSpace: 'nowrap' }} onClick={() => setTyping(true)}>New type</button>
          </div>
        )}
      </div>
      <div className="row2">
        <div className="field">
          <label htmlFor={`${id}-gsm`} className="req">GSM</label>
          <input id={`${id}-gsm`} type="number" min="1" value={gsm} onChange={(e) => setGsm(e.target.value)} />
        </div>
        <div className="field">
          <label className="req">Sheet size (cm)</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="number" min="1" step="0.1" value={length} placeholder="91.5" aria-label="Length in cm"
                   onChange={(e) => setLength(e.target.value)} />
            <span className="dim">×</span>
            <input type="number" min="1" step="0.1" value={width} placeholder="61" aria-label="Width in cm"
                   onChange={(e) => setWidth(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="new-board-foot">
        <span className="hint" style={{ margin: 0 }}>
          {existing ? `${existing.name} is already in your boards — it will be chosen.`
            : name ? `Adds ${name} to your boards.` : problem}
        </span>
        <button className="btn" onClick={close} disabled={busy}>Cancel</button>
        <button className="btn primary" disabled={busy || !!problem} onClick={() => void add()}>
          {existing ? 'Use it' : 'Add board'}
        </button>
      </div>
    </div>
  );
}
